import { useMutation } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { assertIsDefined, type MandateProps } from '@gitbook/util-ts';

import { apiClient, apiQuery, apiQueryClient } from './api';
import { useCurrentUser } from './auth';

const CurrentOrganizationContext = React.createContext<api.Organization | null>(null);

/**
 * Provide the current organization. Either uses the given prop or derives from the screen.
 */
export function CurrentOrganizationProvider(props: {
    organization: api.Organization | null;
    children: React.ReactNode;
}) {
    const { organization, children } = props;
    const currentUser = useCurrentUser();
    const userId = currentUser?.uid ?? null;
    const organizationId = organization?.id;

    React.useEffect(() => {
        analytics.syncLoginCookie(userId && organizationId ? { userId, organizationId } : null);
    }, [organizationId, userId]);

    return (
        <CurrentOrganizationContext.Provider value={organization ?? null}>
            {children}
        </CurrentOrganizationContext.Provider>
    );
}

/**
 * Returns the direct value of the current organization context.
 * Prefer this over useCurrentOrganization if you're in a component that handles an org-less state.
 */
export function useCurrentOrganizationId(): string | null {
    const value = React.useContext(CurrentOrganizationContext);

    if (!value) {
        return null;
    }

    return value.id;
}

/**
 * Returns the current organization ID, and throws an error if the user is not in an organization context.
 * Use this as a lighter alternative to useCurrentOrganization if you only need the id.
 */
export function useAssertCurrentOrganizationId(): string {
    const organizationId = useCurrentOrganizationId();
    assertIsDefined(organizationId, 'you must be in an organization context to use this hook');
    return organizationId;
}

/**
 * Return the current organization. Throws an error if the user is not in an organization screen.
 */
export function useCurrentOrganization(): api.Organization {
    const organization = React.useContext(CurrentOrganizationContext);
    assertIsDefined(organization, 'you must be in an organization context to use this hook');
    return organization;
}

/**
 * Assert that the organization has billing information.
 * Should be used in components that are only accessible to org members.
 */
export function assertOrganizationWithBilling(
    organization: api.Organization
): asserts organization is MandateProps<api.Organization, 'billing'> {
    if (!organization.billing) {
        throw new Error('expected the "billing" property to be defined');
    }
}

/**
 * Organization Join Mutation Hooks
 *
 * These hooks are an EXCEPTION to our typical patterns. We normally rely on
 * subscriptions/mutations at the API level, but here we need SYNCHRONOUS cache
 * removal to prevent a race condition:
 *
 * 1. User joins org → 2. App navigates to org → 3. Application.tsx checks membership
 * → 4. Cache has stale data → 5. Error toast + redirect (false positive)
 *
 * Using removeQueries ensures the stale cache is cleared BEFORE navigation,
 * preventing users from seeing errors after successfully joining.
 */

/**
 * Shared cache removal logic for organization membership changes.
 * Removes stale organization list data after joining/leaving.
 */
function removeOrgMembershipQueries() {
    apiQueryClient.removeQueries({
        queryKey: apiQuery('listOrganizationsForAuthenticatedUser', {}).queryKey,
    });
}

/**
 * Hook to join an organization directly (when user already has permission).
 * Uses removeQueries to clear stale cache data synchronously.
 */
export function useJoinOrganization() {
    return useMutation({
        mutationFn: async (orgId: string) => {
            await apiClient.organizations.joinOrganization(orgId);
            removeOrgMembershipQueries();
        },
    });
}

/**
 * Hook to join an organization with an invite link.
 * Uses removeQueries to clear stale cache data synchronously.
 */
export function useJoinOrganizationWithInvite() {
    return useMutation({
        mutationFn: async ({ orgId, inviteId }: { orgId: string; inviteId: string }) => {
            await apiClient.organizationInvites.joinOrganizationWithInvite(orgId, inviteId);
            removeOrgMembershipQueries();
        },
    });
}
